/**
 * Real logins (piece 21): the rules the invitation, the lockout and the
 * recovery path read, pure over the rows the API hands them, so the screen
 * and the server compute one answer.
 */
import { t } from '../strings';
import { DEFAULT_DEVICE_SESSION_DAYS, DEVICE_SESSION_DAYS_KEY } from './localLogin';

export const LOCKOUT_THRESHOLD_KEY = 'auth.lockoutThreshold';
export const LOCKOUT_WINDOW_KEY = 'auth.lockoutWindowMinutes';
export const RESET_LINK_MINUTES_KEY = 'auth.resetLinkMinutes';
export const RESET_REQUESTS_PER_HOUR_KEY = 'auth.resetRequestsPerHour';
export const DEFAULT_LOCKOUT_THRESHOLD = 5;
export const DEFAULT_LOCKOUT_WINDOW_MINUTES = 15;
export const DEFAULT_RESET_LINK_MINUTES = 60;
export const DEFAULT_RESET_REQUESTS_PER_HOUR = 5;
/**
 * 🔒 Decision 1250: a lockout threshold is a whole number of failed attempts
 * from 1 to 20, the login's and the clock's alike, on the owner's order of
 * 2026-09-28. Below one nothing is ever counted; above twenty a six-digit PIN
 * stands too many guesses a window.
 */
export const LOCKOUT_THRESHOLD_MAX = 20;
/** Decision 1250: the one refusal both lockout thresholds make; a blank is not checked here, because it clears the row back to its default. */
export function lockoutThresholdProblem(value: unknown): string | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= LOCKOUT_THRESHOLD_MAX ? null : `The failed attempts before lockout are a whole number from 1 to ${LOCKOUT_THRESHOLD_MAX}.`;
}
/** Decision 1250: a lockout window is a whole number of minutes, at least one, so a failed attempt is always counted somewhere. */
export function lockoutWindowProblem(value: unknown): string | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 ? null : 'The lockout window is a whole number of minutes, at least 1.';
}
/** A trusted device carries the second factor for this many days after a code verified on it; 0 turns the offer off (decision 0324). */
export const TRUSTED_DEVICE_DAYS_KEY = 'auth.trustedDeviceDays';
export const DEFAULT_TRUSTED_DEVICE_DAYS = 30;
/** An invitation link lives a week; a code delivered by text or in person lives a day. */
export const INVITE_LINK_DAYS = 7;
export const INVITE_CODE_HOURS = 24;
export const RECOVERY_CODE_COUNT = 8;
/** The trash bin's purge (piece 21): bytes leave after a hard 120 days, at the hour the key names. */
export const TRASH_PURGE_HOUR_KEY = 'trash.purgeRunHour';
export const DEFAULT_TRASH_PURGE_HOUR = 3;
export const TRASH_PURGE_DAYS = 120;
/** The nightly reseal of stub-shaped envelopes once a real key is configured (piece 21, Backup, restore and keys; 2026-09-14). */
export const RESEAL_STUB_HOUR_KEY = 'keys.resealRunHour';
export const DEFAULT_RESEAL_STUB_HOUR = 4;
/**
 * The five record types that declare deletedAt and deletedByPersonId (decision 0095); which is a fact read off the schema, listed here for the screen.
 * 🔒 Decision 12970: and the three a super administrator deletes anyway, each held by a `TrashedRecord`.
 */
export const TRASHABLE_RECORD_TYPES = ['File', 'FileVersion', 'CompanyRecord', 'PersonRecord', 'LocationRecord', 'Deal', 'WorkOrder', 'WorkOrderVisit'] as const;
/** 🔒 Decision 12970: the record types the Trash holds by a `TrashedRecord`, their rows written back by Restore. */
export const HELD_RECORD_TYPES = ['Deal', 'WorkOrder', 'WorkOrderVisit'] as const;
export type HeldRecordType = (typeof HELD_RECORD_TYPES)[number];
export type TrashableRecordType = (typeof TRASHABLE_RECORD_TYPES)[number];

export const PIECE_21_SETTINGS: readonly { key: string; value: number }[] = [
  { key: LOCKOUT_THRESHOLD_KEY, value: DEFAULT_LOCKOUT_THRESHOLD },
  { key: LOCKOUT_WINDOW_KEY, value: DEFAULT_LOCKOUT_WINDOW_MINUTES },
  { key: RESET_LINK_MINUTES_KEY, value: DEFAULT_RESET_LINK_MINUTES },
  { key: RESET_REQUESTS_PER_HOUR_KEY, value: DEFAULT_RESET_REQUESTS_PER_HOUR },
  { key: TRASH_PURGE_HOUR_KEY, value: DEFAULT_TRASH_PURGE_HOUR },
  { key: TRUSTED_DEVICE_DAYS_KEY, value: DEFAULT_TRUSTED_DEVICE_DAYS },
  // Decision 4400: the Device-kind session's days, a phone build that holds the lock asks for it.
  { key: DEVICE_SESSION_DAYS_KEY, value: DEFAULT_DEVICE_SESSION_DAYS },
  { key: RESEAL_STUB_HOUR_KEY, value: DEFAULT_RESEAL_STUB_HOUR },
];

/** The two login-creation capabilities; holding the first grants nothing of the second (piece 21). */
export const LOGIN_INTERNAL_CAPABILITY = 'logins.createInternal';
export const LOGIN_EXTERNAL_CAPABILITY = 'logins.createExternal';

/** The six invitation methods in order of trust; the first that fits is used (decision 0130). */
export const INVITE_CHANNELS = ['SSO', 'WorkEmail', 'PersonalEmail', 'Mobile', 'InPerson', 'TemporaryPassword'] as const;
export type InviteChannelName = (typeof INVITE_CHANNELS)[number];

/** A channel that delivers a link the person opens, against one that delivers a code the person types. */
export function channelDeliversLink(channel: InviteChannelName): boolean {
  return channel === 'WorkEmail' || channel === 'PersonalEmail';
}

/**
 * 🔒 What an invitation's delivery needs set up (T3007): company mail's Sending
 * step done for a link by mail, and a text adapter bound for a code by text.
 */
export interface InviteDeliveryFacts {
  /** Company mail's Sending step is done, read as the Set up company mail stepper reads it. */
  mailReady: boolean;
  /** The communications seam has a text adapter bound. */
  textReady: boolean;
}

/**
 * 🔒 The refusal of an invitation on a channel whose provider is not set up, or
 * null (T3007). The sentence names the two hand-over channels, the ways that
 * work now. A provider set up that then rejects a message is not this refusal:
 * that send lands in the failure queue as a not-sent state.
 */
export function inviteChannelProblem(channel: InviteChannelName, facts: InviteDeliveryFacts): string | null {
  if (channelDeliversLink(channel) && !facts.mailReady) return t('settings.logins.mailNotSetUp');
  if (channel === 'Mobile' && !facts.textReady) return t('settings.logins.textNotSetUp');
  return null;
}

/** 🔨 Invited, Active and Deactivated derive from three columns and are never stored (piece 21). */
export type LoginState = 'Invited' | 'Active' | 'Deactivated';

export function loginState(user: { invitedAt: Date | string | null; passwordHash: string | null; deactivatedAt: Date | string | null }): LoginState {
  if (user.deactivatedAt) return 'Deactivated';
  if (user.passwordHash) return 'Active';
  return 'Invited';
}

export interface AttemptRow {
  attemptedAt: Date | string;
  outcome: 'Success' | 'BadPassword' | 'BadFactor' | 'Refused';
}

export interface LockoutVerdict {
  locked: boolean;
  /** Seconds until the oldest counted attempt leaves the window; zero when not locked. */
  secondsLeft: number;
  counted: number;
}

/**
 * 🔨 Lockout is derived from the attempt rows and never stored: `threshold`
 * BadPassword or BadFactor rows inside `windowMinutes` lock the one account,
 * a Refused row extends nothing, and the countdown runs from the oldest row
 * still inside the window. Rows before `since` are not counted: an
 * administrator's unlock is the audit line that moves it.
 */
export function lockoutVerdict(attempts: readonly AttemptRow[], now: Date, threshold: number, windowMinutes: number, since: Date | null = null): LockoutVerdict {
  const windowMs = windowMinutes * 60_000;
  const counted = attempts
    .filter((row) => row.outcome === 'BadPassword' || row.outcome === 'BadFactor')
    .map((row) => new Date(row.attemptedAt).getTime())
    .filter((at) => now.getTime() - at < windowMs && (since === null || at > since.getTime()))
    .sort((a, b) => b - a);
  if (counted.length < threshold) return { locked: false, secondsLeft: 0, counted: counted.length };
  const oldestCounted = counted[threshold - 1]!;
  const secondsLeft = Math.max(1, Math.ceil((oldestCounted + windowMs - now.getTime()) / 1000));
  return { locked: true, secondsLeft, counted: counted.length };
}

/** The approved copy: "Too many failed attempts. Try again in 14:32." */
export function lockoutCountdown(secondsLeft: number): string {
  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/** The recovery channel a delivery leaves behind: methods 3, 4 and 5 create a login with no reset path of their own (decision 0130). */
export function recoveryChannelFor(channel: InviteChannelName, destination: string): { recoveryChannel: InviteChannelName; recoveryDestination: string } | null {
  if (channel === 'PersonalEmail' || channel === 'Mobile') return { recoveryChannel: channel, recoveryDestination: destination };
  if (channel === 'WorkEmail') return { recoveryChannel: 'WorkEmail', recoveryDestination: destination };
  return null;
}

/**
 * What the Invite modal shows in place of the Access Type radio it does not
 * have (decision 0047). 🔨 The sentence comes from the one string lookup
 * (decision 0013) and is written here once: the modal drew it twice until
 * 2026-09-16, once from these two keys and once from a copy of this function's
 * own English, so the same fact was stated by two sources on one screen.
 */
export function accessTypeText(employmentCompanyId: string, organizationCompanyId: string, companyName: string): string {
  return employmentCompanyId === organizationCompanyId ? t('settings.logins.willBeInternal') : `${t('settings.logins.willBePortal')} ${companyName}.`;
}

/**
 * 🔒 An administrator changes which role a login holds, and revokes its access
 * outright, from Settings › People › Logins (decision 0476). Both were missing:
 * `systemRoleId` was written only inside the invitation, and the only
 * deactivation in the system fired when an employment ended — so the one way to
 * revoke somebody's access was to terminate their employment record.
 *
 * 🔨 Every refusal below names the act that clears it (decision 0458).
 */
export interface LoginAdminFacts {
  /** The break-glass administrator, the login that exists so nobody is ever locked out of their own system. */
  isBreakGlass: boolean;
  /** The login already has no access. */
  deactivated: boolean;
  /** The administrator is acting on their own login. */
  isSelf: boolean;
}

/**
 * 🔨 Two logins are never revoked, and the reasons are different. The
 * break-glass login is the way back in when every other login is lost, so
 * revoking it is the one mistake with no recovery. An administrator's own
 * login is refused because the act is instant and its own undo needs the
 * access it just removed.
 */
export function deactivateLoginProblem(facts: LoginAdminFacts): string | null {
  if (facts.isBreakGlass) return 'the break-glass login is how you get back in when every other login is lost, so it is never revoked; revoke the administrator you meant instead';
  if (facts.isSelf) return 'revoking your own access would sign you out with nobody able to undo it; ask another administrator to revoke yours';
  if (facts.deactivated) return 'this login already has no access; restore it first if you meant to change it';
  return null;
}

/** 🔒 Decision 6880: Issue Temporary Password is a super admin's act; the refusal to anybody else. */
export const TEMPORARY_PASSWORD_SUPER_ADMIN_REFUSAL = 'Only a super admin issues a temporary password; ask a super admin to issue this one.';

/** What Issue Temporary Password asks beyond the login's own state (decision 6880). */
export interface TemporaryPasswordFacts extends LoginAdminFacts {
  /** The person acting is a super admin; the sandbox, where every gate answers true, says yes. */
  actorIsSuperAdmin: boolean;
}

/**
 * 🔒 Decision 6880 (T3007): Issue Temporary Password is the way back for a
 * login whose recovery channel is blank or whose mail never arrives. It is
 * refused to anybody who is not a super admin, on the break-glass login, on
 * the actor's own login and on a login with no access, each naming what to do
 * instead. The page and the server read this one function.
 */
export function temporaryPasswordProblem(facts: TemporaryPasswordFacts): string | null {
  if (!facts.actorIsSuperAdmin) return TEMPORARY_PASSWORD_SUPER_ADMIN_REFUSAL;
  if (facts.isBreakGlass) return 'The break-glass login is never issued a temporary password; it changes its own password through its forced change at the machine itself.';
  if (facts.isSelf) return 'You do not issue your own temporary password; change your own password from your account, or ask another super admin.';
  if (facts.deactivated) return 'This login has no access, so it is not issued a password; restore its access first.';
  return null;
}

/** Restoring access is refused only where there is none to restore. */
export function reactivateLoginProblem(facts: Pick<LoginAdminFacts, 'deactivated'>): string | null {
  return facts.deactivated ? null : 'this login already has access; there is nothing to restore';
}

/**
 * 🔒 Decision 1505: how many sign-in, second-step and redeem requests one client address makes in an hour before the
 * rest are refused. They are constants and not Settings rows: they exist to stop a script, and a person typing an
 * authenticator code makes a handful. The sign-in figure is wide enough for an office that arrives at nine.
 */
export const SIGN_IN_PER_ADDRESS_PER_HOUR = 120;
export const CODE_PER_ADDRESS_PER_HOUR = 60;
export const REDEEM_PER_ADDRESS_PER_HOUR = 20;
/** 🔒 Decision 1505: the e-signature callback is open without a session, so each client address is counted on it too. */
export const E_SIGNATURE_CALLBACKS_PER_ADDRESS_PER_HOUR = 60;
export const SIGN_IN_LIMITED_COPY = 'Too many attempts from this address. Try again in an hour.';

/** What a role change asks beyond the login's own state (decision 1502). */
export interface RoleChangeFacts {
  systemRoleId: string | null;
  sameAsNow: boolean;
  /** The login holds the Admin row now, or the change would hand it over. */
  touchesAdminRole: boolean;
  /** The administrator making the change is a super admin (always true in the sandbox, where every gate answers true). */
  actorIsSuperAdmin: boolean;
  /** The role is among those the server offers this login: the invitation's interlock and the portal-eligible rule, asked again. */
  roleOffered: boolean;
}

/**
 * 🔨 A role is never cleared to nothing. Module access comes from the role, so
 * a login holding none can sign in and read nothing at all — a working
 * password on an empty application, which reads as a broken system rather than
 * as revoked access. Revoking access is what `deactivateLoginProblem` guards.
 */
export function changeRoleProblem(facts: LoginAdminFacts & RoleChangeFacts): string | null {
  if (facts.isBreakGlass) return 'the break-glass login holds its own access and takes no role; change the administrator you meant instead';
  // 🔒 Decision 1502: an administrator does not move their own role, whatever it is, so nobody grants themselves the Admin row or hides their own demotion.
  if (facts.isSelf) return 'changing your own role would let you grant yourself access nobody else approved; ask another administrator to change yours';
  if (!facts.systemRoleId) return 'a login reads nothing without a role, so a role is never cleared; pick the role they should hold, or revoke their access instead';
  if (facts.deactivated) return 'this login has no access, so its role changes nothing; restore its access first';
  if (facts.sameAsNow) return 'that is the role they already hold; pick a different one, or leave it as it is';
  // 🔒 Decision 1502: the Admin row is granted and removed by a super admin alone.
  if (facts.touchesAdminRole && !facts.actorIsSuperAdmin) return 'the Admin role is granted and removed by a super admin only; ask a super admin to make this change';
  // 🔨 Decision 0368 held at the invitation and was never asked here: the roles offered for this login are the roles it may take.
  if (!facts.roleOffered) return 'that role is not one this login may hold; pick one the role list offers for it';
  return null;
}
